// ============================================================
// BRAND — JPI Master Design System, and the product's identity
// ============================================================
// The single source of truth for what this application is called and what it
// looks like. Everything visual should resolve back to a token in this file:
// a hex typed directly into a component is a hex nobody can change later.
//
// Two reasons this exists as ONE module rather than as nine copies of a `C`
// object, which is what it replaced:
//
//   1. The palette had drifted. Three of the brand colours were exact and the
//      neutrals were not, and the same near-miss grey-blue appeared under four
//      different names in four files.
//   2. A rename or a re-skin has to be a one-line change or it does not happen.
//
// ── PRODUCT NAME ──────────────────────────────────────────────────────────
// "OOS Tracker" named ONE feature — out-of-sequence detection — at a point when
// that was most of what the app did. It now runs work validation against
// digital captures, inspection reporting, OAC/PSM meetings and capture
// requests, and out-of-sequence flagging is a minority of it. The name is
// declared here so changing it again costs one line.
//
// Two places CANNOT import this and carry their own copy — both are static
// files read before any JavaScript runs. Change them together or the installed
// PWA keeps the old name:
//   • public/manifest.json  → "name" and "short_name"
//   • package.json          → "name" (the npm package id; lowercase, hyphenated)
export const PRODUCT = {
  name: "Milestone Validation Platform",
  // The PWA tile and anywhere the full name will not fit. Deliberately not an
  // acronym: "MVP" reads as minimum-viable-product to everyone who has not been
  // told otherwise.
  shortName: "Milestone",
  description: "Milestone validation, progress tracking and out-of-sequence detection",
  owner: "JPI",
  ownerLine: "JPI · A Sumitomo Forestry Company",
};

// ── COLOUR ────────────────────────────────────────────────────────────────
// The JPI Master Design System's exact values. Never approximate one of these,
// and never introduce a neighbour: a second almost-navy is how the palette
// drifted the first time.
export const JPI = {
  navy:        "#0d2e51", // primary text, headings, dark fills, chart axes
  navy700:     "#1a4a78", // hover states, slightly lighter navy
  navy900:     "#081e38", // deepest navy — footer backgrounds, darkest fills
  paleBlue:    "#e7f0f7", // page/section backgrounds, alternating table rows
  blue:        "#419bc3", // eyebrows, highlight callouts, links, 2nd chart series
  orange:      "#f05123", // ACCENT ONLY — one element per layout, often zero
  orange700:   "#c73e18",
  borderSubtle:"#d1dce7", // dividers, card borders, table lines
  fg1:         "#1c2b3a", // body text on light backgrounds
  fg2:         "#4a6278", // secondary / supporting text, captions
  bgPage:      "#f5f8fb", // default page background (cooler than white)
  white:       "#ffffff",
};

// Chart series, in order. Arbitrary series take these in sequence; orange is
// never the fourth thing that happened to be plotted, it is a deliberate flag.
export const CHART_SERIES = [JPI.navy, JPI.paleBlue, JPI.blue, JPI.orange];
export const CHART_GRID = JPI.borderSubtle;

// ── TYPOGRAPHY ────────────────────────────────────────────────────────────
// Museo Sans and Aptos are the licensed faces; neither is servable from a
// public CDN, so the stacks name them first and fall through to the
// substitutions the design system itself nominates.
//
// DISPLAY carries titles, headings, eyebrows, callout numbers, nav labels,
// table headers and chips. BODY carries paragraphs, cell copy and captions.
//
// There is no mono role in the JPI system, and this app leaned on one heavily —
// every label, chip and column of figures. Those are DISPLAY work in the JPI
// vocabulary, so `mono` resolves to the display stack; what the mono face was
// actually contributing to the takt grid was digit alignment, and that is
// `tabularNums` below rather than a whole typeface.
export const FONT = {
  display: "'Museo Sans', 'Inter', 'Source Sans 3', system-ui, sans-serif",
  body: "'Aptos Body', 'Aptos', 'Calibri', 'Segoe UI', system-ui, sans-serif",
  // Kept as a named role because hundreds of call sites reference it, and
  // because naming it says WHY those sites are display-weight rather than
  // leaving a bare font stack inline.
  mono: "'Museo Sans', 'Inter', 'Source Sans 3', system-ui, sans-serif",
  tabularNums: "tabular-nums",
};

// Weights, so a call site says what it means rather than a number.
export const WEIGHT = { light: 300, regular: 500, bold: 700, black: 900 };

// ── SHAPE, DEPTH, MOTION ──────────────────────────────────────────────────
export const RADIUS = { button: 4, card: 8, pill: 999 };
export const SHADOW = {
  sm: "0 1px 3px rgba(13,46,81,0.08), 0 1px 2px rgba(13,46,81,0.05)",
  md: "0 4px 12px rgba(13,46,81,0.12), 0 2px 4px rgba(13,46,81,0.06)",
};
export const MOTION = { duration: "180ms", easeOut: "cubic-bezier(0.16, 1, 0.3, 1)" };
export const LAYOUT = { containerMax: 1200, containerPad: "clamp(20px, 4vw, 64px)" };

// ── THE SHARED PALETTE ────────────────────────────────────────────────────
// What the components consume. Key names are the ones the nine former `C`
// objects already used, so this is a substitution rather than a rewrite of
// every call site — `C.navy`, `C.border`, `C.muted` and the rest keep working
// and now resolve to JPI tokens.
export const C = {
  navy:    JPI.navy,
  navy700: JPI.navy700,
  navy900: JPI.navy900,
  blue:    JPI.blue,
  orange:  JPI.orange,
  white:   JPI.white,

  // Neutrals — where the drift was. `border` was #dce8f4, `bg` was #f0f5fa and
  // `muted` was #8aa4bc; all three were near-misses of a JPI token.
  border:  JPI.borderSubtle,
  bg:      JPI.bgPage,
  panel2:  JPI.paleBlue,   // the tint band / alternating row fill
  muted:   JPI.fg2,        // secondary text. Also fixes a real accessibility
                           // problem: the old #8aa4bc read 2.36:1 on the page
                           // background, under even the large-text floor.
  text:    JPI.fg1,        // body copy on light backgrounds

  // ── SEMANTIC STATUS ──────────────────────────────────────────────────────
  // NOT brand decoration. These are a CODE the reader decodes — a flag's
  // severity, a layer's progress, a validation outcome — and the design
  // system's "limit yourself to the brand colours" rule is about chrome, not
  // about data encoding. Remapping these to navy and pale blue would render
  // beautifully and destroy the only thing several screens exist to say.
  //
  // They are here so there is ONE definition of each, and so it is obvious at a
  // glance which colours in this app are brand and which are meaning.
  // ONE green and ONE amber. There were three of each: success was #1a9e6e in
  // six files and #16a34a in a seventh, warn was #d97706, #b45309 and #f59e0b
  // depending on which file you were in, and a third green (#22c55e) had crept
  // into the takt cells. Three shades for one meaning is not a palette, it is
  // drift — and the reader cannot tell whether two nearly-identical greens are
  // saying the same thing or two different things.
  //
  // Chosen by CONTRAST rather than by majority: #15803d and #b45309 clear
  // 4.7:1 on the page background, where the incumbents sat at 3.2 and 3.0 —
  // under the 4.5:1 the design system's own body-text rule implies.
  good:    "#15803d",
  success: "#15803d",
  warn:    "#b45309",
  crit:    "#c0392b",
  danger:  "#c0392b",
  violet:  "#6d4bd8",

  // Tints — the same meanings as washes behind a badge or a cell. Each is its
  // status colour at low saturation, which is why they are not brand tokens
  // either.
  successBg: "#e6f7ef",
  warnBg:    "#fef3c7",
  dangerBg:  "#fee2e2",
  flagBg:    "#fde8e0",
  error:     "#c0392b",
  errorBg:   "#fef3f2",
  emptyCell: "#b6c2d1",

  // ── LIVE / LOADING ────────────────────────────────────────────────────────
  // The header status dot, and it is a different job from the status TEXT
  // colours above. A dot 8px across with a glow behind it has to be BRIGHT to
  // read as "live" at all — the contrast argument that picked the darker green
  // for text runs the other way here, because nothing is written on it.
  //
  // Named, because a global sweep unified this dot into the text green and
  // quietly turned the app's one liveness indicator dull. A colour with a job
  // this specific should be impossible to hit by accident.
  live:        "#22c55e",
  liveLoading: "#f59e0b",

  // The same meanings, on a NAVY ground. The header and the sidebar are dark,
  // and #15803d on navy is unreadable — so "good" there is a light green, for
  // the same reason the live dot is a bright one. Named so it stops looking
  // like a fourth green somebody left behind.
  goodOnDark:  "#86efac",

  // "This was never in scope" — pre-program work, captured before a project's
  // Validation Start Date. Not a status a layer can be in and not a severity,
  // which is why neither the brand palette nor the status colours had a word
  // for it. The workflow diagrams need one.
  outOfScope:  "#7a3c1a",

  // Aliases the nine former palettes used for JPI tokens under other names.
  lightBlue: JPI.paleBlue,
  card:      JPI.white,
};

// A JPI eyebrow: the small uppercase label above a heading.
export const eyebrow = (color = JPI.blue) => ({
  fontFamily: FONT.display, fontWeight: WEIGHT.bold, fontSize: 11,
  textTransform: "uppercase", letterSpacing: "0.12em", color,
});

// A JPI chip / tag.
export const chip = (color = JPI.navy, background = JPI.paleBlue) => ({
  fontFamily: FONT.display, fontWeight: WEIGHT.bold, fontSize: 10,
  textTransform: "uppercase", letterSpacing: "0.08em",
  borderRadius: RADIUS.pill, padding: "2px 9px", color, background,
});

// A wash of a status colour: the same hue behind text rather than as text.
//
// These two roles genuinely differ — a wash must be pale enough to sit under
// copy, a text colour dark enough to be read — so forcing them to one hex is
// wrong. What is NOT allowed is for them to be different HUES, which is what
// happened when the solid ambers were unified and their rgba washes, written as
// literals, stayed behind on the old colour. Deriving the wash from the token
// makes them provably the same colour.
export const tint = (hex, alpha) => {
  const n = parseInt(String(hex).slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};
